import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  Typography,
  SelectChangeEvent,
  Chip,
  IconButton
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';

// Internal imports
import { TrueViewTableProps } from './types/TrueViewTable.types';
import { tableStyles, colors } from './styles/TrueViewTable.styles';
import { WEEK_LABELS } from './constants/TrueViewTable.constants';
import { useTrueViewData } from './hooks/useTrueViewData';
import { TabButton, LoadingSpinner } from './components/UIComponents';
import { WeekHeaders, SubHeaderRow } from './components/TableHeaders';
import { generateWeekCells } from './utils/TableCells.utils';
import { getWeeklyEvaluationData, getStoresByEvaluationCount } from './utils/WeeklyEvalUtils';
import { startAutoLogout, stopAutoLogout } from './utils/AutoLogout';
import {
  getStoresForMarket,
  getGlobalDate,
  getUnitNumber,
  getState,
  getDMEvaluationDate,
  getLatestDMScore,
  getDistrictManager,
  getWeekEvaluations,
  getWeekDateRanges
} from './utils/TrueViewTable.utils';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

// Type definitions
type StoreType = {
  "Store Number": number;
  "Store Name": string;
  "DM": string;
};

const TrueViewTable: React.FC<TrueViewTableProps> = ({ newData, onGoBack, errorMessage, onNavigateToEmailStatus }) => {
  // State management
  const [activeTab, setActiveTab] = useState<string>('Summary');
  const [selectedMonth, setSelectedMonth] = useState<string>((new Date().getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [appliedMonth, setAppliedMonth] = useState<string>((new Date().getMonth() + 1).toString());
  const [appliedYear, setAppliedYear] = useState<string>(new Date().getFullYear().toString());
  const [completionFilter, setCompletionFilter] = useState<string>('All');
  const [pullMarketFilter, setPullMarketFilter] = useState<string>('All');
  const [dmCompletionFilter, setDmCompletionFilter] = useState<string>('All');
  const [activeDmTab, setActiveDmTab] = useState<string>('All');
  const [selectedDM, setSelectedDM] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showMoreWeeks, setShowMoreWeeks] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedWeeklySummaryMarket, setSelectedWeeklySummaryMarket] = useState<string | null>(null);
  const [expandedEvaluationCount, setExpandedEvaluationCount] = useState<string | null>(null);
  const [showRestaurantFilter, setShowRestaurantFilter] = useState(false);
  const [restaurantNameFilter, setRestaurantNameFilter] = useState('');
  const [showUnitFilter, setShowUnitFilter] = useState(false);
  const [unitFilter, setUnitFilter] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState<number | null>(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
  const [weeklySubTab, setWeeklySubTab] = useState<'current' | 'previous'>('current');
  const [tabLoading, setTabLoading] = useState(false);
  const handleWeeklySubTabChange = useCallback((newTab: 'current' | 'previous') => {
    setWeeklySubTab(newTab);
  }, []);

  // Handle tab change with loading
  const handleTabChange = useCallback((newTab: string) => {
    if (newTab === activeTab) return; // Prevent unnecessary re-renders
    setTabLoading(true);
    // Use requestAnimationFrame for smoother transitions
    requestAnimationFrame(() => {
      setActiveTab(newTab);
      setTabLoading(false);
    });
  }, [activeTab]);

  // Constants for virtualization
  const rowHeight = 60; // Height of each table row in pixels

  // Custom hook for data fetching
  const {
    marketData,
    evaluationData,
    extendedEvaluationData,
    dataError,
    evaluationLoading,
    marketLoading,
    lastUpdated
  } = useTrueViewData(appliedMonth, appliedYear);

  // Local state to manage data clearing
  const [localMarketData, setMarketData] = useState<any>(null);
  const [localEvaluationData, setEvaluationData] = useState<any>(null);
  const [localExtendedEvaluationData, setExtendedEvaluationData] = useState<any>(null);

  // Update local data when API data changes
  useEffect(() => {
    if (marketData) setMarketData(marketData);
  }, [marketData]);

  useEffect(() => {
    if (evaluationData) setEvaluationData(evaluationData);
  }, [evaluationData]);

  useEffect(() => {
    if (extendedEvaluationData) setExtendedEvaluationData(extendedEvaluationData);
  }, [extendedEvaluationData]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-logout functionality
  useEffect(() => {
    startAutoLogout();
    return () => stopAutoLogout();
  }, []);

  // Memoize processed markets
  const processedMarkets = useMemo(() => {
    const markets = localMarketData?.data ? Object.keys(localMarketData.data) : [];
    return markets.map(market => {
      if (market === 'NC West' || market === 'NC East') return 'North Carolina';
      if (market === 'OHWV' || market === 'Pitts') return 'Tri-State';
      if (market === 'VA Beach') return 'Virginia';
      return market;
    }).filter((market, index, arr) => arr.indexOf(market) === index);
  }, [localMarketData]);

  // Handle apply button click
  const handleApply = useCallback(() => {
    // Clear existing data when filters change
    setMarketData(null);
    setEvaluationData(null);
    setExtendedEvaluationData(null);
    setAppliedMonth(selectedMonth);
    setAppliedYear(selectedYear);
  }, [selectedMonth, selectedYear]);

  // Pre-process and cache all store data to avoid repeated calculations
  const processedStoreData = useMemo(() => {
    if (!localMarketData || !localEvaluationData || !localExtendedEvaluationData) return {};
    
    const storeCache: { [storeNumber: number]: any } = {};
    
    processedMarkets.forEach(market => {
      const marketStores = getStoresForMarket(market, localMarketData);
      marketStores.forEach(store => {
        const storeNumber = store["Store Number"];
        if (!storeCache[storeNumber]) {
          storeCache[storeNumber] = {
            storeNumber,
            storeName: store["Store Name"],
            dm: store["DM"] || 'N/A',
            market,
            globalDate: getGlobalDate(storeNumber, localEvaluationData, newData),
            unitNumber: getUnitNumber(storeNumber, localEvaluationData, newData),
            state: getState(storeNumber, localEvaluationData, newData),
            dmEvaluationDate: getDMEvaluationDate(storeNumber, localEvaluationData, newData, appliedMonth, appliedYear),
            latestDMScore: getLatestDMScore(storeNumber, localExtendedEvaluationData, appliedMonth, appliedYear),
            districtManager: getDistrictManager(storeNumber, localEvaluationData, newData),
            weekEvaluations: getWeekEvaluations(storeNumber, localExtendedEvaluationData, appliedMonth, appliedYear),
            lastWeekEvaluations: getWeekEvaluations(storeNumber, localExtendedEvaluationData, appliedMonth, appliedYear, true)
          };
        }
      });
    });
    
    return storeCache;
  }, [localMarketData, localEvaluationData, localExtendedEvaluationData, newData, appliedMonth, appliedYear, processedMarkets]);

  // Optimized utility functions using cached data
  const utilityFunctions = useMemo(() => {
    if (!processedStoreData || Object.keys(processedStoreData).length === 0) {
      return {
        getStoresForMarket: () => [],
        getGlobalDate: () => 'N/A',
        getUnitNumber: () => 'N/A',
        getState: () => 'N/A',
        getDMEvaluationDate: () => 'N/A',
        getLatestDMScore: () => 'N/A',
        getDistrictManager: () => 'N/A',
        getWeekEvaluations: () => [],
        getLastWeekEvaluations: () => [],
        getWeekDateRanges: () => []
      };
    }
    
    const storesByMarket = new Map();
    
    return {
      getStoresForMarket: (market: string) => {
        if (!storesByMarket.has(market)) {
          const stores = Object.values(processedStoreData).filter((store: any) => store.market === market);
          const result = stores.map((store: any) => ({
            "Store Number": store.storeNumber,
            "Store Name": store.storeName,
            "DM": store.dm
          }));
          storesByMarket.set(market, result);
        }
        return storesByMarket.get(market);
      },
      getGlobalDate: (storeNumber: number) => processedStoreData[storeNumber]?.globalDate || 'N/A',
      getUnitNumber: (storeNumber: number) => processedStoreData[storeNumber]?.unitNumber || 'N/A',
      getState: (storeNumber: number) => processedStoreData[storeNumber]?.state || 'N/A',
      getDMEvaluationDate: (storeNumber: number) => processedStoreData[storeNumber]?.dmEvaluationDate || 'N/A',
      getLatestDMScore: (storeNumber: number) => processedStoreData[storeNumber]?.latestDMScore || 'N/A',
      getDistrictManager: (storeNumber: number) => processedStoreData[storeNumber]?.districtManager || 'N/A',
      getWeekEvaluations: (storeNumber: number) => processedStoreData[storeNumber]?.weekEvaluations || [],
      getLastWeekEvaluations: (storeNumber: number) => processedStoreData[storeNumber]?.lastWeekEvaluations || [],
      getWeekDateRanges: () => getWeekDateRanges(appliedMonth, appliedYear)
    };
  }, [processedStoreData, appliedMonth, appliedYear]);

  // Pre-calculate weekly summary data with memoization
  const weeklyData = useMemo(() => {
    if (!processedMarkets.length || !utilityFunctions.getStoresForMarket) return { current: [], previous: [] };
    
    const currentWeekData = processedMarkets.map(market => {
      const marketStores = utilityFunctions.getStoresForMarket(market);
      if (!marketStores.length) return { market, counts: { threeComplete: 0, twoComplete: 0, oneComplete: 0, zeroComplete: 0 } };
      
      return {
        market,
        counts: getWeeklyEvaluationData(marketStores, utilityFunctions.getWeekEvaluations).counts
      };
    });
    
    const previousWeekData = processedMarkets.map(market => {
      const marketStores = utilityFunctions.getStoresForMarket(market);
      if (!marketStores.length) return { market, counts: { threeComplete: 0, twoComplete: 0, oneComplete: 0, zeroComplete: 0 } };
      
      return {
        market,
        counts: getWeeklyEvaluationData(marketStores, utilityFunctions.getLastWeekEvaluations).counts
      };
    });
    
    return { current: currentWeekData, previous: previousWeekData };
  }, [processedMarkets, utilityFunctions.getStoresForMarket, utilityFunctions.getWeekEvaluations, utilityFunctions.getLastWeekEvaluations]);

  // Handle Excel export
  const handleExportToExcel = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();

    // Summary Tab
    const summarySheet = workbook.addWorksheet('Summary');
    summarySheet.columns = [
      { header: 'Market', key: 'market', width: 30 },
      { header: 'Stores', key: 'stores', width: 20 },
      { header: 'Pending', key: 'pending', width: 20 },
      { header: 'Rate', key: 'rate', width: 20 }
    ];
    
    // Style header
    summarySheet.getRow(1).eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.alignment = { horizontal: 'center' };
    });
    
    processedMarkets.forEach(market => {
      const marketStores = utilityFunctions.getStoresForMarket(market);
      const totalStores = marketStores.length;
      const completedStores = marketStores.filter((store: StoreType) => utilityFunctions.getGlobalDate(store["Store Number"]) !== 'N/A').length;
      const notCompleted = totalStores - completedStores;
      const completionRate = totalStores > 0 ? Math.round((completedStores / totalStores) * 100) : 0;
      
      const row = summarySheet.addRow({
        market, stores: totalStores, pending: notCompleted, 
        rate: `${completionRate}%`
      });
      
      // Color code rows based on completion rate
      const bgColor = completionRate >= 80 ? 'FF90EE90' : completionRate >= 50 ? 'FFFFFF00' : 'FFFF6B6B';
      row.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        cell.alignment = { horizontal: 'center' };
      });
    });
    
    // Add total row
    const totalAllStores = processedMarkets.reduce((sum, market) => {
      return sum + utilityFunctions.getStoresForMarket(market).length;
    }, 0);
    const totalCompleted = processedMarkets.reduce((sum, market) => {
      return sum + utilityFunctions.getStoresForMarket(market).filter((store: StoreType) => utilityFunctions.getGlobalDate(store["Store Number"]) !== 'N/A').length;
    }, 0);
    const totalNotCompleted = totalAllStores - totalCompleted;
    const overallCompletionRate = totalAllStores > 0 ? Math.round((totalCompleted / totalAllStores) * 100) : 0;
    
    const totalRow = summarySheet.addRow({
      market: 'TOTAL', stores: totalAllStores, pending: totalNotCompleted,
      rate: `${overallCompletionRate}%`
    });
    
    totalRow.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
      cell.font = { bold: true };
      cell.border = { top: { style: 'thick', color: { argb: 'FF374151' } } };
      cell.alignment = { horizontal: 'center' };
    });

    // Weekly Summary - Current Week Tab
    const weeklyCurrentSheet = workbook.addWorksheet('Weekly Summary - Current');
    weeklyCurrentSheet.columns = [
      { header: 'Market', key: 'market', width: 30 },
      { header: 'Fully Complete (3/3)', key: 'fullyComplete', width: 25 },
      { header: 'Mostly Complete (2/3)', key: 'mostlyComplete', width: 25 },
      { header: 'Partially Complete (1/3)', key: 'partiallyComplete', width: 28 },
      { header: 'Not Started (0/3)', key: 'notStarted', width: 22 }
    ];
    
    weeklyCurrentSheet.getRow(1).eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.alignment = { horizontal: 'center' };
    });
    
    weeklyData.current.forEach(({ market, counts }) => {
      weeklyCurrentSheet.addRow({
        market,
        fullyComplete: counts.threeComplete,
        mostlyComplete: counts.twoComplete,
        partiallyComplete: counts.oneComplete,
        notStarted: counts.zeroComplete
      });
      
      const lastRow = weeklyCurrentSheet.lastRow;
      if (lastRow) {
        lastRow.eachCell(cell => {
          cell.alignment = { horizontal: 'center' };
        });
      }
    });
    
    // Add total row for current week
    const currentTotalCounts = weeklyData.current.reduce((acc, { counts }) => ({
      threeComplete: acc.threeComplete + counts.threeComplete,
      twoComplete: acc.twoComplete + counts.twoComplete,
      oneComplete: acc.oneComplete + counts.oneComplete,
      zeroComplete: acc.zeroComplete + counts.zeroComplete
    }), { threeComplete: 0, twoComplete: 0, oneComplete: 0, zeroComplete: 0 });
    
    const weeklyCurrentTotalRow = weeklyCurrentSheet.addRow({
      market: 'TOTAL',
      fullyComplete: currentTotalCounts.threeComplete,
      mostlyComplete: currentTotalCounts.twoComplete,
      partiallyComplete: currentTotalCounts.oneComplete,
      notStarted: currentTotalCounts.zeroComplete
    });
    
    weeklyCurrentTotalRow.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
      cell.font = { bold: true };
      cell.border = { top: { style: 'thick', color: { argb: 'FF374151' } } };
      cell.alignment = { horizontal: 'center' };
    });

    // Weekly Summary - Previous Week Tab
    const weeklyPreviousSheet = workbook.addWorksheet('Weekly Summary - Previous');
    weeklyPreviousSheet.columns = [
      { header: 'Market', key: 'market', width: 30 },
      { header: 'Fully Complete (3/3)', key: 'fullyComplete', width: 25 },
      { header: 'Mostly Complete (2/3)', key: 'mostlyComplete', width: 25 },
      { header: 'Partially Complete (1/3)', key: 'partiallyComplete', width: 28 },
      { header: 'Not Started (0/3)', key: 'notStarted', width: 22 }
    ];
    
    weeklyPreviousSheet.getRow(1).eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.alignment = { horizontal: 'center' };
    });
    
    weeklyData.previous.forEach(({ market, counts }) => {
      weeklyPreviousSheet.addRow({
        market,
        fullyComplete: counts.threeComplete,
        mostlyComplete: counts.twoComplete,
        partiallyComplete: counts.oneComplete,
        notStarted: counts.zeroComplete
      });
      
      const lastRow = weeklyPreviousSheet.lastRow;
      if (lastRow) {
        lastRow.eachCell(cell => {
          cell.alignment = { horizontal: 'center' };
        });
      }
    });
    
    // Add total row for previous week
    const previousTotalCounts = weeklyData.previous.reduce((acc, { counts }) => ({
      threeComplete: acc.threeComplete + counts.threeComplete,
      twoComplete: acc.twoComplete + counts.twoComplete,
      oneComplete: acc.oneComplete + counts.oneComplete,
      zeroComplete: acc.zeroComplete + counts.zeroComplete
    }), { threeComplete: 0, twoComplete: 0, oneComplete: 0, zeroComplete: 0 });
    
    const weeklyPreviousTotalRow = weeklyPreviousSheet.addRow({
      market: 'TOTAL',
      fullyComplete: previousTotalCounts.threeComplete,
      mostlyComplete: previousTotalCounts.twoComplete,
      partiallyComplete: previousTotalCounts.oneComplete,
      notStarted: previousTotalCounts.zeroComplete
    });
    
    weeklyPreviousTotalRow.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
      cell.font = { bold: true };
      cell.border = { top: { style: 'thick', color: { argb: 'FF374151' } } };
      cell.alignment = { horizontal: 'center' };
    });

    // Pull Tab
    const pullSheet = workbook.addWorksheet('Pull');
    pullSheet.columns = [
      { header: 'Restaurant', key: 'restaurant', width: 45 },
      { header: 'Unit', key: 'unit', width: 18 },
      { header: 'State', key: 'state', width: 18 },
      { header: 'Market', key: 'market', width: 30 },
      { header: 'District Manager', key: 'dm', width: 30 },
      { header: 'Global Date', key: 'globalDate', width: 25 }
    ];
    
    pullSheet.getRow(1).eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB7C5DC' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.alignment = { horizontal: 'center' };
    });
    
    processedMarkets.forEach(market => {
      const marketStores = utilityFunctions.getStoresForMarket(market);
      marketStores.forEach((store: StoreType) => {
        const globalDate = utilityFunctions.getGlobalDate(store["Store Number"]);
        if (globalDate !== 'N/A') {
          pullSheet.addRow({
            restaurant: `${store["Store Number"]} - ${store["Store Name"]}`,
            unit: utilityFunctions.getUnitNumber(store["Store Number"]),
            state: (() => {
            const state = utilityFunctions.getState(store["Store Number"]);
            if (['OH', 'WV', 'PA'].includes(state)) return 'TS';
            if (state === 'IN') return 'NY';
            return state;
          })(),
            market,
            dm: utilityFunctions.getDistrictManager(store["Store Number"]),
            globalDate
          });
          
          // Center align the data
          const lastRow = pullSheet.lastRow;
          if (lastRow) {
            lastRow.eachCell(cell => {
              cell.alignment = { horizontal: 'center' };
            });
          }
        }
      });
    });

    // Individual Market Tabs
    processedMarkets.forEach(market => {
      const marketSheet = workbook.addWorksheet(market);
      const weekRanges = utilityFunctions.getWeekDateRanges();
      
      // Create columns with sub-headers for each week
      const columns = [
        { header: 'Restaurant', key: 'restaurant', width: 45 },
        { header: 'Unit', key: 'unit', width: 18 },
        { header: 'State', key: 'state', width: 18 },
        { header: 'District Manager', key: 'dm', width: 30 },
        { header: 'DM Evaluation', key: 'dmEval', width: 25 },
        { header: 'Latest DM Score', key: 'dmScore', width: 25 },
        { header: 'Global Date', key: 'globalDate', width: 25 }
      ];
      
      // Add week columns with sub-headers
      for (let i = 0; i < 5; i++) {
        columns.push(
          { header: `Week ${i + 1} Eval 1 Title`, key: `w${i + 1}e1title`, width: 25 },
          { header: `Week ${i + 1} Eval 1 Score`, key: `w${i + 1}e1score`, width: 20 },
          { header: `Week ${i + 1} Eval 1 Date`, key: `w${i + 1}e1date`, width: 22 },
          { header: `Week ${i + 1} Eval 2 Title`, key: `w${i + 1}e2title`, width: 25 },
          { header: `Week ${i + 1} Eval 2 Score`, key: `w${i + 1}e2score`, width: 20 },
          { header: `Week ${i + 1} Eval 2 Date`, key: `w${i + 1}e2date`, width: 22 },
          { header: `Week ${i + 1} Eval 3 Title`, key: `w${i + 1}e3title`, width: 25 },
          { header: `Week ${i + 1} Eval 3 Score`, key: `w${i + 1}e3score`, width: 20 },
          { header: `Week ${i + 1} Eval 3 Date`, key: `w${i + 1}e3date`, width: 22 }
        );
      }
      
      columns.push({ header: 'Status', key: 'status', width: 22 });
      marketSheet.columns = columns;
      
      // Style main headers
      marketSheet.getRow(1).eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6585BC' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: 'center' };
      });
      
      // Add week range headers as merged cells
      let colIndex = 8; // Starting after Global Date column
      for (let i = 0; i < 5; i++) {
        marketSheet.mergeCells(1, colIndex, 1, colIndex + 8);
        marketSheet.getCell(1, colIndex).value = `Week ${i + 1} (${weekRanges[i]})`;
        marketSheet.getCell(1, colIndex).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6585BC' } };
        marketSheet.getCell(1, colIndex).font = { bold: true, color: { argb: 'FFFFFFFF' } };
        marketSheet.getCell(1, colIndex).alignment = { horizontal: 'center' };
        colIndex += 9;
      }
      
      // Add sub-header row
      const subHeaderRow = marketSheet.addRow([
        'Restaurant', 'Unit', 'State', 'District Manager', 'DM Evaluation', 'Latest DM Score', 'Global Date',
        'Eval 1 Title', 'Eval 1 Score', 'Eval 1 Date', 'Eval 2 Title', 'Eval 2 Score', 'Eval 2 Date', 'Eval 3 Title', 'Eval 3 Score', 'Eval 3 Date',
        'Eval 1 Title', 'Eval 1 Score', 'Eval 1 Date', 'Eval 2 Title', 'Eval 2 Score', 'Eval 2 Date', 'Eval 3 Title', 'Eval 3 Score', 'Eval 3 Date',
        'Eval 1 Title', 'Eval 1 Score', 'Eval 1 Date', 'Eval 2 Title', 'Eval 2 Score', 'Eval 2 Date', 'Eval 3 Title', 'Eval 3 Score', 'Eval 3 Date',
        'Eval 1 Title', 'Eval 1 Score', 'Eval 1 Date', 'Eval 2 Title', 'Eval 2 Score', 'Eval 2 Date', 'Eval 3 Title', 'Eval 3 Score', 'Eval 3 Date',
        'Eval 1 Title', 'Eval 1 Score', 'Eval 1 Date', 'Eval 2 Title', 'Eval 2 Score', 'Eval 2 Date', 'Eval 3 Title', 'Eval 3 Score', 'Eval 3 Date',
        'Status'
      ]);
      
      subHeaderRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB0C4DE' } };
        cell.font = { bold: true, color: { argb: 'FF000000' } };
        cell.alignment = { horizontal: 'center' };
      });
      
      const marketStores = utilityFunctions.getStoresForMarket(market);
      marketStores.forEach((store: StoreType) => {
        const globalDate = utilityFunctions.getGlobalDate(store["Store Number"]);
        const status = globalDate !== 'N/A' ? 'COMPLETED' : 'PENDING';
        const weekEvals = utilityFunctions.getWeekEvaluations(store["Store Number"]);
        
        const rowData: any = {
          restaurant: `${store["Store Number"]} - ${store["Store Name"]}`,
          unit: utilityFunctions.getUnitNumber(store["Store Number"]),
          state: (() => {
            const state = utilityFunctions.getState(store["Store Number"]);
            if (['OH', 'WV', 'PA'].includes(state)) return 'TS';
            if (state === 'IN') return 'NY';
            return state;
          })(),
          dm: store["DM"] || 'N/A',
          dmEval: utilityFunctions.getDMEvaluationDate(store["Store Number"]),
          dmScore: utilityFunctions.getLatestDMScore(store["Store Number"]),
          globalDate,
          status
        };
        
        // Add week evaluation data
        for (let weekIndex = 0; weekIndex < 5; weekIndex++) {
          const weekData = weekEvals[weekIndex];
          for (let evalIndex = 0; evalIndex < 3; evalIndex++) {
            const evaluation = weekData?.evaluations?.[evalIndex];
            const prefix = `w${weekIndex + 1}e${evalIndex + 1}`;
            rowData[`${prefix}title`] = evaluation?.title || 'N/A';
            rowData[`${prefix}score`] = evaluation?.score || 'N/A';
            rowData[`${prefix}date`] = evaluation?.date || 'N/A';
          }
        }
        
        const row = marketSheet.addRow(rowData);
        
        // Color code based on completion status
        const bgColor = status === 'COMPLETED' ? 'FFF0F8FF' : 'FFF8D7DA';
        row.eachCell(cell => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
          cell.alignment = { horizontal: 'center' };
        });
      });
      
      // Add total row for each market
      const totalStores = marketStores.length;
      const completedStores = marketStores.filter((store: StoreType) => utilityFunctions.getGlobalDate(store["Store Number"]) !== 'N/A').length;
      const totalRowData: any = {
        restaurant: 'TOTAL',
        unit: `${totalStores} Stores (${completedStores} Completed)`,
        state: '-',
        dm: '-',
        dmEval: '-',
        dmScore: '-',
        globalDate: '-',
        status: '-'
      };
      
      // Add dashes for all week evaluation columns
      for (let weekIndex = 0; weekIndex < 5; weekIndex++) {
        for (let evalIndex = 0; evalIndex < 3; evalIndex++) {
          const prefix = `w${weekIndex + 1}e${evalIndex + 1}`;
          totalRowData[`${prefix}title`] = '-';
          totalRowData[`${prefix}score`] = '-';
          totalRowData[`${prefix}date`] = '-';
        }
      }
      
      const totalRow = marketSheet.addRow(totalRowData);
      
      totalRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
        cell.font = { bold: true };
        cell.border = { top: { style: 'thick', color: { argb: 'FF374151' } } };
        cell.alignment = { horizontal: 'center' };
      });
    });

    // Download Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TrueView_Report_${appliedMonth}_${appliedYear}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [processedMarkets, utilityFunctions, appliedMonth, appliedYear]);

  // Memoized DM Data with early return
  const dmData = useMemo(() => {
    if (!processedStoreData || Object.keys(processedStoreData).length === 0) return [];
    
    const dmMap = new Map();
    
    Object.values(processedStoreData).forEach((store: any) => {
      const dm = store.dm;
      const market = store.market;
      
      if (dm !== 'N/A') {
        if (!dmMap.has(dm)) {
          dmMap.set(dm, { totalStores: 0, notCompleted: 0, market });
        }
        const dmInfo = dmMap.get(dm);
        dmInfo.totalStores++;
        
        if (store.globalDate === 'N/A') {
          dmInfo.notCompleted++;
        }
      }
    });
    
    return Array.from(dmMap.entries()).map(([dm, data]) => ({ dm, ...data }));
  }, [processedStoreData]);

  // Loading state - show loader until all data is ready
  const isDataReady = localMarketData && localEvaluationData && localExtendedEvaluationData && 
                      Object.keys(processedStoreData).length > 0;
  
  const [allTabsReady, setAllTabsReady] = useState(false);
  
  useEffect(() => {
    if (isDataReady && weeklyData.current.length > 0 && dmData.length >= 0) {
      const timer = setTimeout(() => {
        setAllTabsReady(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isDataReady, weeklyData, dmData]);
  
  if (evaluationLoading || marketLoading || !isDataReady || !allTabsReady) {
    return (
      <div style={{
        ...tableStyles.container,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <LoadingSpinner message="Fetching data..." />
      </div>
    );
  }

  // Error state
  if (dataError || errorMessage) {
    return (
      <div style={tableStyles.container}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={tableStyles.card}>
            <div style={{ ...tableStyles.header, padding: '24px' }}>
              <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
                TrueView Evaluations
              </h1>
            </div>
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <h3 style={{ fontSize: '18px', color: colors.danger, margin: '0 0 8px 0' }}>
                Data Not Found
              </h3>
              <p style={{ fontSize: '14px', color: colors.secondary, margin: 0 }}>
                {dataError || errorMessage}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...tableStyles.container, padding: isMobile ? '10px' : '20px' }}>
      <div style={{ maxWidth: isMobile ? '100%' : '1400px', margin: '0 auto' }}>
        <div style={tableStyles.card}>
          {/* Header */}
          <div style={{ ...tableStyles.header, padding: isMobile ? '16px' : '24px' }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '16px' : '0'
            }}>
              <div>
                <h1 style={{
                  color: 'white',
                  fontSize: isMobile ? '24px' : '28px',
                  fontWeight: 'bold',
                  margin: '0 0 8px 0'
                }}>
                  TrueView Evaluations
                </h1>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  margin: 0,
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '500'
                }}>
                  Restaurant-data
                </p>
                {lastUpdated && (
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    margin: '4px 0 0 0',
                    fontSize: isMobile ? '11px' : '12px',
                    fontWeight: '400'
                  }}>
                    Last updated: {lastUpdated}
                  </p>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: isMobile ? 'stretch' : 'flex-end', 
                gap: '12px', 
                flexWrap: 'wrap' 
              }}>
                {/* Date Controls */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  flexWrap: 'wrap',
                  marginTop: isMobile ? '8px' : '0'
                }}>
                  <label style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: '500',
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    Month:
                  </label>
                  <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 12px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '14px',
                    outline: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#1f2937'
                  }}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <label style={{
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '500',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}>
                  Year:
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 12px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '14px',
                    outline: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#1f2937'
                  }}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
                <button
                  onClick={handleApply}
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    backgroundColor: colors.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Apply
                </button>
                </div>
                
                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  justifyContent: isMobile ? 'stretch' : 'flex-end',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={handleExportToExcel}
                    style={{
                      padding: isMobile ? '6px 27px' : '8px 34px',
                      backgroundColor: '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      flex: isMobile ? '1' : 'none'
                    }}
                  >
                    📊 Export Excel
                  </button>
                  <button
                    onClick={() => {
                      localStorage.clear();
                      sessionStorage.clear();
                      window.location.href = '/login';
                    }}
                    style={{
                      padding: isMobile ? '6px 27px' : '8px 34px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      flex: isMobile ? '1' : 'none'
                    }}
                  >
                    🚪 Logout
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: isMobile ? '16px' : '24px' }}>
            {/* Tab Navigation */}
            <div style={{
              borderBottom: '2px solid #e5e7eb',
              marginBottom: '24px',
              display: 'flex',
              gap: '4px',
              overflowX: 'auto',
              paddingBottom: '8px',
              scrollbarWidth: 'thin',
              WebkitOverflowScrolling: 'touch',
              minHeight: isMobile ? '50px' : '60px',
              position: 'relative'
            }}
            className="tab-navigation"
            >
              <style>
                {`
                  .tab-navigation::-webkit-scrollbar {
                    height: 8px;
                  }
                  .tab-navigation::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 4px;
                  }
                  .tab-navigation::-webkit-scrollbar-thumb {
                    background: #cbd5e0;
                    border-radius: 4px;
                  }
                  .tab-navigation::-webkit-scrollbar-thumb:hover {
                    background: #9ca3af;
                  }
                `}
              </style>
              <TabButton
                active={activeTab === 'Summary'}
                onClick={() => handleTabChange('Summary')}
                isMobile={isMobile}
              >
                Summary
              </TabButton>
              <TabButton
                active={activeTab === 'Weekly Summary'}
                onClick={() => handleTabChange('Weekly Summary')}
                isMobile={isMobile}
              >
                Weekly Summary
              </TabButton>
              <TabButton
                active={activeTab === 'Pull'}
                onClick={() => handleTabChange('Pull')}
                isMobile={isMobile}
              >
                Pull
              </TabButton>
              {processedMarkets.map(market => (
                <TabButton
                  key={market}
                  active={activeTab === market}
                  onClick={() => handleTabChange(market)}
                  isMobile={isMobile}
                >
                  {market}
                </TabButton>
              ))}
            </div>

            {/* Tab Content */}
            {tabLoading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px'
              }}>
                <LoadingSpinner message="Loading tab content..." />
              </div>
            ) : activeTab === 'Summary' ? (
              <>
                <TableContainer component={Paper} sx={{ 
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  overflowX: 'auto'
                }}>
                  <Table sx={{ minWidth: isMobile ? 'auto' : 650 }}>
                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px',
                          fontSize: isMobile ? '12px' : '14px',
                          padding: isMobile ? '8px 4px' : '16px'
                        }}>Market</TableCell>
                        <TableCell align="center" sx={{ 
                          fontWeight: 600, 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px',
                          fontSize: isMobile ? '12px' : '14px',
                          padding: isMobile ? '8px 4px' : '16px'
                        }}>Stores</TableCell>
                        <TableCell align="center" sx={{ 
                          fontWeight: 600, 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px',
                          fontSize: isMobile ? '12px' : '14px',
                          padding: isMobile ? '8px 4px' : '16px'
                        }}>Pending</TableCell>
                        <TableCell align="center" sx={{ 
                          fontWeight: 600, 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px',
                          fontSize: isMobile ? '12px' : '14px',
                          padding: isMobile ? '8px 4px' : '16px'
                        }}>Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {processedMarkets.map(market => {
                        const marketStores = utilityFunctions.getStoresForMarket(market);
                        const totalStores = marketStores.length;
                        const completedStores = marketStores.filter((store: StoreType) => utilityFunctions.getGlobalDate(store["Store Number"]) !== 'N/A').length;
                        const notCompleted = totalStores - completedStores;
                        const completionRate = totalStores > 0 ? Math.round((completedStores / totalStores) * 100) : 0;
                        
                        return (
                          <TableRow key={market} sx={{ 
                            backgroundColor: completionRate < 50 ? '#ffcccc' : 
                                           completionRate < 80 ? '#eceecdff' : '#acebe2ff'
                          }}>
                            <TableCell sx={{ 
                              fontSize: isMobile ? '13px' : '15px', 
                              fontWeight: 500,
                              padding: isMobile ? '8px 4px' : '16px'
                            }}>{market}</TableCell>
                            <TableCell align="center" sx={{ 
                              fontSize: isMobile ? '13px' : '15px', 
                              fontWeight: 500,
                              padding: isMobile ? '8px 4px' : '16px'
                            }}>{totalStores}</TableCell>
                            <TableCell align="center" sx={{ 
                              fontSize: isMobile ? '13px' : '15px', 
                              fontWeight: 500,
                              color: notCompleted > 0 ? '#dc2626' : '#16a34a',
                              padding: isMobile ? '8px 4px' : '16px'
                            }}>{notCompleted}</TableCell>
                            <TableCell align="center" sx={{ 
                              fontSize: isMobile ? '13px' : '15px', 
                              fontWeight: 600,
                              color: completionRate < 50 ? '#dc2626' : completionRate < 80 ? '#85321eff' : '#16a34a',
                              padding: isMobile ? '8px 4px' : '16px'
                            }}>{completionRate}%</TableCell>
                          </TableRow>
                        );
                      })}
                      {(() => {
                        const totalAllStores = processedMarkets.reduce((sum, market) => {
                          return sum + utilityFunctions.getStoresForMarket(market).length;
                        }, 0);
                        const totalCompleted = processedMarkets.reduce((sum, market) => {
                          return sum + utilityFunctions.getStoresForMarket(market).filter((store: StoreType) => utilityFunctions.getGlobalDate(store["Store Number"]) !== 'N/A').length;
                        }, 0);
                        const totalNotCompleted = totalAllStores - totalCompleted;
                        const overallCompletionRate = totalAllStores > 0 ? Math.round((totalCompleted / totalAllStores) * 100) : 0;
                        
                        return (
                          <TableRow sx={{ 
                            borderTop: '2px solid #374151',
                            backgroundColor: '#f1f5f9'
                          }}>
                            <TableCell sx={{ fontSize: '16px', fontWeight: 700 }}>TOTAL</TableCell>
                            <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>{totalAllStores}</TableCell>
                            <TableCell align="center" sx={{ 
                              fontSize: '16px', 
                              fontWeight: 700,
                              color: totalNotCompleted > 0 ? '#dc2626' : '#16a34a'
                            }}>{totalNotCompleted}</TableCell>
                            <TableCell align="center" sx={{ 
                              fontSize: '16px', 
                              fontWeight: 700,
                              color: overallCompletionRate < 50 ? '#dc2626' : overallCompletionRate < 80 ? '#85321eff' : '#16a34a'
                            }}>{overallCompletionRate}%</TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* District Manager Summary */}
                <div style={{ 
                  marginTop: '32px',
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  maxHeight: '500px',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{ 
                    padding: '16px 20px', 
                    backgroundColor: '#f8fafc', 
                    borderBottom: '1px solid #e5e7eb',
                    flexShrink: 0
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#374151'
                      }}>District Manager Summary</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Status:</label>
                        <select
                          value={dmCompletionFilter}
                          onChange={(e) => setDmCompletionFilter(e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '12px',
                            outline: 'none',
                            backgroundColor: 'white',
                            color: '#374151',
                            minWidth: '120px'
                          }}
                        >
                          <option value="All">All DMs</option>
                          <option value="Completed">All Completed</option>
                          <option value="Not Completed">Has Incomplete</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', overflowX: 'auto' }}>
                      <TabButton
                        active={activeDmTab === 'All'}
                        onClick={() => setActiveDmTab('All')}
                      >
                        All
                      </TabButton>
                      {processedMarkets.map(market => (
                        <TabButton
                          key={market}
                          active={activeDmTab === market}
                          onClick={() => setActiveDmTab(market)}
                        >
                          {market}
                        </TabButton>
                      ))}
                    </div>
                  </div>
                  <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>District Manager</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Market</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Stores</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Completed</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Not Completed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dmData.filter(dmInfo => {
                          const marketMatch = activeDmTab === 'All' || dmInfo.market === activeDmTab;
                          let completionMatch = true;
                          if (dmCompletionFilter === 'Completed') {
                            completionMatch = dmInfo.notCompleted === 0;
                          } else if (dmCompletionFilter === 'Not Completed') {
                            completionMatch = dmInfo.notCompleted > 0;
                          }
                          return marketMatch && completionMatch;
                        }).map((dmInfo, index) => (
                          <React.Fragment key={index}>
                            <TableRow sx={{ 
                              backgroundColor: dmInfo.notCompleted > 0 ? '#fff5f5' : '#f0fdf4'
                            }}>
                              <TableCell 
                                sx={{ 
                                  fontSize: '15px', 
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }} 
                                onClick={() => setSelectedDM(selectedDM === dmInfo.dm ? null : dmInfo.dm)}
                              >
                                <IconButton size="small">
                                  {selectedDM === dmInfo.dm ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                                {dmInfo.dm}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '15px', fontWeight: 500 }}>{dmInfo.market}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '15px', fontWeight: 500 }}>{dmInfo.totalStores}</TableCell>
                              <TableCell align="center" sx={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: (dmInfo.totalStores - dmInfo.notCompleted) > 0 ? '#16a34a' : '#6b7280'
                              }}>{dmInfo.totalStores - dmInfo.notCompleted}</TableCell>
                              <TableCell align="center" sx={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: dmInfo.notCompleted > 0 ? '#dc2626' : '#16a34a'
                              }}>{dmInfo.notCompleted}</TableCell>
                            </TableRow>
                            {selectedDM === dmInfo.dm && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ p: 0, backgroundColor: '#f8fafc' }}>
                                  <div style={{ 
                                    margin: '0 20px 20px 20px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    overflow: 'hidden'
                                  }}>
                                    <div style={{
                                      padding: '12px 16px',
                                      backgroundColor: '#f1f5f9',
                                      borderBottom: '1px solid #e5e7eb',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#374151'
                                    }}>
                                      {dmInfo.dm}'s Restaurants
                                    </div>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                                          <TableCell sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Restaurant</TableCell>
                                          <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>State</TableCell>
                                          <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Unit</TableCell>
                                          <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Status</TableCell>
                                          <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Global Date</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {(() => {
                                          const dmStores: any[] = [];
                                          processedMarkets.forEach(market => {
                                            const marketStores = utilityFunctions.getStoresForMarket(market);
                                            marketStores.forEach((store: StoreType) => {
                                              const storeDM = store["DM"] || 'N/A';
                                              if (storeDM === dmInfo.dm) {
                                                dmStores.push({ ...store, market });
                                              }
                                            });
                                          });
                                          
                                          return dmStores.map((store, storeIndex) => {
                                            const globalDate = utilityFunctions.getGlobalDate(store["Store Number"]);
                                            const unit = utilityFunctions.getUnitNumber(store["Store Number"]);
                                            const isCompleted = globalDate !== 'N/A';
                                            
                                            return (
                                              <TableRow key={storeIndex} sx={{ 
                                                backgroundColor: storeIndex % 2 === 0 ? '#fafafa' : 'white'
                                              }}>
                                                <TableCell sx={{ fontSize: '14px' }}>
                                                  {store["Store Number"]} - {store["Store Name"]}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: '14px' }}>
                                                  {(() => {
                                                    const state = utilityFunctions.getState(store["Store Number"]);
                                                    if (['OH', 'WV', 'PA'].includes(state)) return 'TS';
                                                    if (state === 'IN') return 'NY';
                                                    return state;
                                                  })()}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: '14px' }}>
                                                  {unit}
                                                </TableCell>
                                                <TableCell align="center">
                                                  <Chip 
                                                    label={isCompleted ? 'Complete' : 'Pending'}
                                                    size="small"
                                                    sx={{
                                                      backgroundColor: isCompleted ? '#dcfce7' : '#fee2e2',
                                                      color: isCompleted ? '#166534' : '#991b1b',
                                                      fontWeight: 600,
                                                      textTransform: 'uppercase'
                                                    }}
                                                  />
                                                </TableCell>
                                                <TableCell align="center" sx={{ 
                                                  fontSize: '14px',
                                                  color: isCompleted ? '#16a34a' : '#dc2626',
                                                  fontWeight: 500
                                                }}>
                                                  {globalDate}
                                                </TableCell>
                                              </TableRow>
                                            );
                                          });
                                        })()}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </>
            ) : activeTab === 'Weekly Summary' ? (
              <>
                {/* Weekly Summary Sub-tabs */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '16px',
                  borderBottom: '1px solid #e5e7eb',
                  paddingBottom: '8px'
                }}>
                  <TabButton
                    active={weeklySubTab === 'current'}
                    onClick={() => handleWeeklySubTabChange('current')}
                    isMobile={isMobile}
                  >
                    Current Week <span style={{ fontSize: '10px', fontWeight: 400 }}>({utilityFunctions.getWeekDateRanges()[0]})</span>
                  </TabButton>
                  <TabButton
                    active={weeklySubTab === 'previous'}
                    onClick={() => handleWeeklySubTabChange('previous')}
                    isMobile={isMobile}
                  >
                    Previous Week <span style={{ fontSize: '10px', fontWeight: 400 }}>({utilityFunctions.getWeekDateRanges()[1]})</span>
                  </TabButton>
                </div>
                <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                <div style={{ padding: '12px 16px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                    💡 Click on any count number to view the stores in that completion category
                  </Typography>
                </div>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Market</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <div>Fully Complete</div>
                        <div style={{ fontSize: '10px', fontWeight: 400, textTransform: 'none', opacity: 0.8, marginTop: '2px' }}>3 of 3 Evaluations</div>
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <div>Mostly Complete</div>
                        <div style={{ fontSize: '10px', fontWeight: 400, textTransform: 'none', opacity: 0.8, marginTop: '2px' }}>2 of 3 Evaluations</div>
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <div>Partially Complete</div>
                        <div style={{ fontSize: '10px', fontWeight: 400, textTransform: 'none', opacity: 0.8, marginTop: '2px' }}>1 of 3 Evaluations</div>
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <div>Not Started</div>
                        <div style={{ fontSize: '10px', fontWeight: 400, textTransform: 'none', opacity: 0.8, marginTop: '2px' }}>0 of 3 Evaluations</div>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(weeklySubTab === 'current' ? weeklyData.current : weeklyData.previous).map(({ market, counts }) => {
                      
                      return (
                        <React.Fragment key={market}>
                          <TableRow>
                            <TableCell sx={{ fontSize: '15px', fontWeight: 500 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => setSelectedWeeklySummaryMarket(selectedWeeklySummaryMarket === market ? null : market)}
                                >
                                  {selectedWeeklySummaryMarket === market ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                                {market}
                              </div>
                            </TableCell>
                            <TableCell align="center" sx={{ 
                              fontSize: '15px', 
                              fontWeight: 600,
                              color: '#16a34a',
                              backgroundColor: '#dcfce7',
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }} onClick={() => setExpandedEvaluationCount(expandedEvaluationCount === `${market}-3` ? null : `${market}-3`)}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                {counts.threeComplete}
                                <IconButton size="small">
                                  {expandedEvaluationCount === `${market}-3` ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </div>
                            </TableCell>
                            <TableCell align="center" sx={{ 
                              fontSize: '15px', 
                              fontWeight: 600,
                              color: '#059669',
                              backgroundColor: '#d1fae5',
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }} onClick={() => setExpandedEvaluationCount(expandedEvaluationCount === `${market}-2` ? null : `${market}-2`)}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                {counts.twoComplete}
                                <IconButton size="small">
                                  {expandedEvaluationCount === `${market}-2` ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </div>
                            </TableCell>
                            <TableCell align="center" sx={{ 
                              fontSize: '15px', 
                              fontWeight: 600,
                              color: '#d97706',
                              backgroundColor: '#fef3c7',
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }} onClick={() => setExpandedEvaluationCount(expandedEvaluationCount === `${market}-1` ? null : `${market}-1`)}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                {counts.oneComplete}
                                <IconButton size="small">
                                  {expandedEvaluationCount === `${market}-1` ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </div>
                            </TableCell>
                            <TableCell align="center" sx={{ 
                              fontSize: '15px', 
                              fontWeight: 600,
                              color: '#dc2626',
                              backgroundColor: '#fee2e2',
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }} onClick={() => setExpandedEvaluationCount(expandedEvaluationCount === `${market}-0` ? null : `${market}-0`)}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                {counts.zeroComplete}
                                <IconButton size="small">
                                  {expandedEvaluationCount === `${market}-0` ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </div>
                            </TableCell>
                          </TableRow>
                          {selectedWeeklySummaryMarket === market && (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ p: 0, backgroundColor: '#f8fafc' }}>
                                <div style={{ 
                                  margin: '0 20px 20px 20px',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  backgroundColor: 'white',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#f1f5f9',
                                    borderBottom: '1px solid #e5e7eb',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151'
                                  }}>
                                    {market} - All Store Evaluation Details
                                  </div>
                                  <div style={{ display: 'flex', gap: '20px', padding: '16px' }}>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 8px 0', color: '#16a34a', fontSize: '14px' }}>Fully Complete ({counts.threeComplete})</h4>
                                      <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px' }}>
                                        {getStoresByEvaluationCount(utilityFunctions.getStoresForMarket(market), weeklySubTab === 'current' ? utilityFunctions.getWeekEvaluations : utilityFunctions.getLastWeekEvaluations, 3).map(store => (
                                          <div key={store["Store Number"]} style={{ padding: '2px 0', color: '#374151' }}>
                                            <div style={{ fontWeight: '500' }}>{store["Store Number"]} - {store["Store Name"]}</div>
                                            {store.evaluationDates && <div style={{ fontSize: '10px', color: '#6b7280', marginLeft: '8px' }}>Dates: {store.evaluationDates}</div>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 8px 0', color: '#059669', fontSize: '14px' }}>Mostly Complete ({counts.twoComplete})</h4>
                                      <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px' }}>
                                        {getStoresByEvaluationCount(utilityFunctions.getStoresForMarket(market), weeklySubTab === 'current' ? utilityFunctions.getWeekEvaluations : utilityFunctions.getLastWeekEvaluations, 2).map(store => (
                                          <div key={store["Store Number"]} style={{ padding: '2px 0', color: '#374151' }}>
                                            <div style={{ fontWeight: '500' }}>{store["Store Number"]} - {store["Store Name"]}</div>
                                            {store.evaluationDates && <div style={{ fontSize: '10px', color: '#6b7280', marginLeft: '8px' }}>Dates: {store.evaluationDates}</div>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 8px 0', color: '#d97706', fontSize: '14px' }}>Partially Complete ({counts.oneComplete})</h4>
                                      <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px' }}>
                                        {getStoresByEvaluationCount(utilityFunctions.getStoresForMarket(market), weeklySubTab === 'current' ? utilityFunctions.getWeekEvaluations : utilityFunctions.getLastWeekEvaluations, 1).map(store => (
                                          <div key={store["Store Number"]} style={{ padding: '2px 0', color: '#374151' }}>
                                            <div style={{ fontWeight: '500' }}>{store["Store Number"]} - {store["Store Name"]}</div>
                                            {store.evaluationDates && <div style={{ fontSize: '10px', color: '#6b7280', marginLeft: '8px' }}>Dates: {store.evaluationDates}</div>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 8px 0', color: '#dc2626', fontSize: '14px' }}>Not Started ({counts.zeroComplete})</h4>
                                      <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px' }}>
                                        {getStoresByEvaluationCount(utilityFunctions.getStoresForMarket(market), weeklySubTab === 'current' ? utilityFunctions.getWeekEvaluations : utilityFunctions.getLastWeekEvaluations, 0).map((store: any) => (
                                          <div key={store["Store Number"]} style={{ padding: '2px 0', color: '#374151' }}>
                                            <div style={{ fontWeight: '500' }}>{store["Store Number"]} - {store["Store Name"]}</div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                          {expandedEvaluationCount?.startsWith(market) && (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ p: 0, backgroundColor: '#f8fafc' }}>
                                <div style={{ 
                                  margin: '0 20px 20px 20px',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  backgroundColor: 'white',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#f1f5f9',
                                    borderBottom: '1px solid #e5e7eb',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#374151'
                                  }}>
                                    {market} - Store Evaluation Details
                                  </div>
                                  <div style={{ padding: '16px' }}>
                                    {expandedEvaluationCount === `${market}-3` && (
                                      <div>
                                        <h4 style={{ margin: '0 0 8px 0', color: '#16a34a', fontSize: '14px' }}>3 Complete Evaluations ({counts.threeComplete})</h4>
                                        <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px' }}>
                                          {getStoresByEvaluationCount(utilityFunctions.getStoresForMarket(market), weeklySubTab === 'current' ? utilityFunctions.getWeekEvaluations : utilityFunctions.getLastWeekEvaluations, 3).map((store: any) => (
                                            <div key={store["Store Number"]} style={{ padding: '2px 0', color: '#374151' }}>
                                              <div style={{ fontWeight: '500' }}>{store["Store Number"]} - {store["Store Name"]}</div>
                                              {store.evaluationDates && <div style={{ fontSize: '10px', color: '#6b7280', marginLeft: '8px' }}>Dates: {store.evaluationDates}</div>}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {expandedEvaluationCount === `${market}-2` && (
                                      <div>
                                        <h4 style={{ margin: '0 0 8px 0', color: '#059669', fontSize: '14px' }}>2 Complete Evaluations ({counts.twoComplete})</h4>
                                        <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px' }}>
                                          {getStoresByEvaluationCount(utilityFunctions.getStoresForMarket(market), weeklySubTab === 'current' ? utilityFunctions.getWeekEvaluations : utilityFunctions.getLastWeekEvaluations, 2).map((store: any) => (
                                            <div key={store["Store Number"]} style={{ padding: '2px 0', color: '#374151' }}>
                                              <div style={{ fontWeight: '500' }}>{store["Store Number"]} - {store["Store Name"]}</div>
                                              {store.evaluationDates && <div style={{ fontSize: '10px', color: '#6b7280', marginLeft: '8px' }}>Dates: {store.evaluationDates}</div>}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {expandedEvaluationCount === `${market}-1` && (
                                      <div>
                                        <h4 style={{ margin: '0 0 8px 0', color: '#d97706', fontSize: '14px' }}>1 Complete Evaluation ({counts.oneComplete})</h4>
                                        <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px' }}>
                                          {getStoresByEvaluationCount(utilityFunctions.getStoresForMarket(market), weeklySubTab === 'current' ? utilityFunctions.getWeekEvaluations : utilityFunctions.getLastWeekEvaluations, 1).map((store: any) => (
                                            <div key={store["Store Number"]} style={{ padding: '2px 0', color: '#374151' }}>
                                              <div style={{ fontWeight: '500' }}>{store["Store Number"]} - {store["Store Name"]}</div>
                                              {store.evaluationDates && <div style={{ fontSize: '10px', color: '#6b7280', marginLeft: '8px' }}>Dates: {store.evaluationDates}</div>}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {expandedEvaluationCount === `${market}-0` && (
                                      <div>
                                        <h4 style={{ margin: '0 0 8px 0', color: '#dc2626', fontSize: '14px' }}>0 Evaluations ({counts.zeroComplete})</h4>
                                        <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px' }}>
                                          {getStoresByEvaluationCount(utilityFunctions.getStoresForMarket(market), weeklySubTab === 'current' ? utilityFunctions.getWeekEvaluations : utilityFunctions.getLastWeekEvaluations, 0).map((store: any) => (
                                            <div key={store["Store Number"]} style={{ padding: '2px 0', color: '#374151' }}>
                                              <div style={{ fontWeight: '500' }}>{store["Store Number"]} - {store["Store Name"]}</div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {(() => {
                      const data = weeklySubTab === 'current' ? weeklyData.current : weeklyData.previous;
                      const totalCounts = data.reduce((acc, { counts }) => ({
                          threeComplete: acc.threeComplete + counts.threeComplete,
                          twoComplete: acc.twoComplete + counts.twoComplete,
                          oneComplete: acc.oneComplete + counts.oneComplete,
                          zeroComplete: acc.zeroComplete + counts.zeroComplete
                        }), { threeComplete: 0, twoComplete: 0, oneComplete: 0, zeroComplete: 0 });
                      
                      return (
                        <TableRow sx={{ 
                          borderTop: '2px solid #374151',
                          backgroundColor: '#f1f5f9'
                        }}>
                          <TableCell sx={{ fontSize: '16px', fontWeight: 700 }}>TOTAL</TableCell>
                          <TableCell align="center" sx={{ 
                            fontSize: '16px', 
                            fontWeight: 700,
                            color: '#16a34a',
                            backgroundColor: '#dcfce7'
                          }}>{totalCounts.threeComplete}</TableCell>
                          <TableCell align="center" sx={{ 
                            fontSize: '16px', 
                            fontWeight: 700,
                            color: '#059669',
                            backgroundColor: '#d1fae5'
                          }}>{totalCounts.twoComplete}</TableCell>
                          <TableCell align="center" sx={{ 
                            fontSize: '16px', 
                            fontWeight: 700,
                            color: '#d97706',
                            backgroundColor: '#fef3c7'
                          }}>{totalCounts.oneComplete}</TableCell>
                          <TableCell align="center" sx={{ 
                            fontSize: '16px', 
                            fontWeight: 700,
                            color: '#dc2626',
                            backgroundColor: '#fee2e2'
                          }}>{totalCounts.zeroComplete}</TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            ) : activeTab === 'Pull' ? (
              <>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Filter by Market:</Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={pullMarketFilter}
                      onChange={(e: SelectChangeEvent) => setPullMarketFilter(e.target.value)}
                    >
                      <MenuItem value="All">All Markets</MenuItem>
                      {processedMarkets.map(market => (
                        <MenuItem key={market} value={market}>{market}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#b7c5dcff' }}>
                        <TableCell sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2
                        }}>Restaurant</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2,
                          paddingTop: '18px',
                          paddingLeft: '21px'
                        }}>Unit</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2
                        }}>State</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2
                        }}>Market</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2
                        }}>District Manager</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2
                        }}>Global Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const allCompletedStores: any[] = [];
                        processedMarkets.forEach(market => {
                          const marketStores = utilityFunctions.getStoresForMarket(market);
                          marketStores.forEach((store: StoreType) => {
                            const globalDate = utilityFunctions.getGlobalDate(store["Store Number"]);
                            if (globalDate !== 'N/A') {
                              allCompletedStores.push({ ...store, market });
                            }
                          });
                        });
                        
                        const filteredStores = pullMarketFilter === 'All' 
                          ? allCompletedStores 
                          : allCompletedStores.filter(store => store.market === pullMarketFilter);
                        
                        return filteredStores.map((store, index) => (
                          <TableRow key={index} sx={{ backgroundColor: '#f0f8ff' }}>
                            <TableCell>
                              {store["Store Number"]} - {store["Store Name"]}
                            </TableCell>
                            <TableCell align="center">
                              {utilityFunctions.getUnitNumber(store["Store Number"])}
                            </TableCell>
                            <TableCell align="center">
                              {(() => {
                                const state = utilityFunctions.getState(store["Store Number"]);
                                if (['OH', 'WV', 'PA'].includes(state)) return 'TS';
                                if (state === 'IN') return 'NY';
                                return state;
                              })()}
                            </TableCell>
                            <TableCell align="center">
                              {store.market}
                            </TableCell>
                            <TableCell align="center">
                              {utilityFunctions.getDistrictManager(store["Store Number"])}
                            </TableCell>
                            <TableCell align="center">
                              {utilityFunctions.getGlobalDate(store["Store Number"])}
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                      <TableRow sx={{ 
                        borderTop: '2px solid #374151',
                        backgroundColor: '#f1f5f9'
                      }}>
                        <TableCell sx={{ fontSize: '16px', fontWeight: 700 }}>TOTAL</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>-</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>-</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>-</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>-</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>
                          {(() => {
                            const allCompletedStores: any[] = [];
                            processedMarkets.forEach(market => {
                              const marketStores = utilityFunctions.getStoresForMarket(market);
                              marketStores.forEach((store: StoreType) => {
                                const globalDate = utilityFunctions.getGlobalDate(store["Store Number"]);
                                if (globalDate !== 'N/A') {
                                  allCompletedStores.push({ ...store, market });
                                }
                              });
                            });
                            const filteredStores = pullMarketFilter === 'All' 
                              ? allCompletedStores 
                              : allCompletedStores.filter(store => store.market === pullMarketFilter);
                            return filteredStores.length;
                          })()} Completed
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : activeTab && processedMarkets.includes(activeTab) ? (
              <>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Filter by Status:</Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={completionFilter}
                      onChange={(e: SelectChangeEvent) => setCompletionFilter(e.target.value)}
                    >
                      <MenuItem value="All">All Stores</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Not Completed">Not Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <div 
                  className="top-scroller"
                  onScroll={(e) => {
                    const tableContainer = document.querySelector('.table-container') as HTMLDivElement;
                    if (tableContainer) {
                      tableContainer.scrollLeft = e.currentTarget.scrollLeft;
                    }
                  }}
                  style={{ 
                    width: '100%', 
                    height: isMobile ? '15px' : '20px', 
                    overflowX: 'auto', 
                    overflowY: 'hidden',
                    marginBottom: isMobile ? '12px' : '16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    backgroundColor: '#f8fafc',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  <div style={{ 
                    width: `${1400 + (showMoreWeeks ? 10 : 5) * 9 * 130 + 1800}px`, 
                    height: isMobile ? '13px' : '18px',
                    backgroundColor: 'transparent'
                  }}></div>
                </div>
                <TableContainer 
                  className="table-container"
                  onScroll={(e) => {
                    const topScroller = document.querySelector('.top-scroller') as HTMLDivElement;
                    if (topScroller) {
                      topScroller.scrollLeft = e.currentTarget.scrollLeft;
                    }
                  }}
                  component={Paper} 
                  sx={{ 
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
                    borderRadius: 2, 
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': {
                      height: isMobile ? '8px' : '12px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#cbd5e0',
                      borderRadius: '6px'
                    }
                  }}
                >
                  <Table sx={{ minWidth: `${1400 + (showMoreWeeks ? 10 : 5) * 9 * 130 + 1800}px` }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#6585bcff' }}>
                        <TableCell 
                          rowSpan={2} 
                          sx={{ 
                            fontWeight: 700, 
                            textTransform: 'uppercase', 
                            letterSpacing: '1px', 
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: '14px',
                            py: 2,
                            position: 'sticky',
                            left: 0,
                            backgroundColor: '#6585bcff',
                            zIndex: 10,
                            '&:hover': { backgroundColor: '#6580afff' }
                          }}
                          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            Restaurant {sortOrder === 'asc' ? '↑' : '↓'}
                            <IconButton 
                              size="small" 
                              sx={{ color: 'white' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowRestaurantFilter(!showRestaurantFilter);
                              }}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                          </div>
                          {showRestaurantFilter && (
                            <div style={{ marginTop: 8 }}>
                              <input
                                type="text"
                                placeholder="Filter restaurants..."
                                value={restaurantNameFilter}
                                onChange={(e) => setRestaurantNameFilter(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  width: '140px'
                                }}
                              />
                            </div>
                          )}
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2,
                          textAlign: 'center',
                          display: 'none'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                            Unit
                            <IconButton 
                              size="small" 
                              sx={{ color: 'white' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowUnitFilter(!showUnitFilter);
                              }}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                          </div>
                          {showUnitFilter && (
                            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
                              <input
                                type="text"
                                placeholder="Filter units..."
                                value={unitFilter}
                                onChange={(e) => setUnitFilter(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  width: '100px'
                                }}
                              />
                            </div>
                          )}
                        </TableCell>
                        <TableCell rowSpan={2} sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2,
                          textAlign: 'center'
                        }}>State</TableCell>
                        <TableCell rowSpan={2} sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2,
                          textAlign: 'center'
                        }}>District Manager</TableCell>
                        <TableCell rowSpan={2} sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2,
                          textAlign: 'center'
                        }}>DM Evaluation</TableCell>
                        <TableCell rowSpan={2} sx={{ 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          letterSpacing: '1px',
                          color: 'white',
                          fontSize: '14px',
                          py: 2,
                          textAlign: 'center'
                        }}>Latest DM Score</TableCell>

                        {WEEK_LABELS.slice(0, 5).map((week, index) => {
                          const weekRanges = utilityFunctions.getWeekDateRanges();
                          return (
                            <TableCell 
                              key={week} 
                              colSpan={9} 
                              align="center"
                              sx={{
                                p: 2,
                                fontSize: '16px',
                                fontWeight: 700,
                                color: 'white',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                minWidth: '400px',
                                borderLeft: '2px solid rgba(255,255,255,0.3)'
                              }}
                            >
                              {week} ({weekRanges[index]})
                            </TableCell>
                          );
                        })}
                        <WeekHeaders 
                          showMoreWeeks={showMoreWeeks}
                          setShowMoreWeeks={setShowMoreWeeks}
                          getWeekDateRanges={utilityFunctions.getWeekDateRanges}
                        />
                      </TableRow>
                      <SubHeaderRow showMoreWeeks={showMoreWeeks} />
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const currentStores = utilityFunctions.getStoresForMarket(activeTab);
                        const dateSortedStores = [...currentStores].sort((a, b) => {
                          const aNumber = a["Store Number"];
                          const bNumber = b["Store Number"];
                          return sortOrder === 'asc' ? aNumber - bNumber : bNumber - aNumber;
                        });
                        
                        let filteredStores = dateSortedStores;
                        if (completionFilter === 'Completed') {
                          filteredStores = dateSortedStores.filter(store => utilityFunctions.getGlobalDate(store["Store Number"]) !== 'N/A');
                        } else if (completionFilter === 'Not Completed') {
                          filteredStores = dateSortedStores.filter(store => utilityFunctions.getGlobalDate(store["Store Number"]) === 'N/A');
                        }
                        
                        if (restaurantNameFilter) {
                          filteredStores = filteredStores.filter(store => 
                            store["Store Name"].toLowerCase().includes(restaurantNameFilter.toLowerCase()) ||
                            store["Store Number"].toString().includes(restaurantNameFilter)
                          );
                        }
                        
                        if (unitFilter) {
                          filteredStores = filteredStores.filter(store => {
                            const unit = utilityFunctions.getUnitNumber(store["Store Number"]);
                            return unit.toString().includes(unitFilter);
                          });
                        }
                        
                        const visibleStores = filteredStores;
                        
                        return visibleStores.map((store, index) => {
                          const unit = utilityFunctions.getUnitNumber(store["Store Number"]);
                          const globalDate = utilityFunctions.getGlobalDate(store["Store Number"]);
                          const isIncomplete = unit === 'N/A' || globalDate === 'N/A';
                          const isCompleted = globalDate !== 'N/A';
                          const weekEvals = utilityFunctions.getWeekEvaluations(store["Store Number"]);
                          
                          return (
                            <TableRow key={index} sx={{ 
                              backgroundColor: selectedRestaurant === store["Store Number"] ? '#1976d2' :
                                             isIncomplete ? '#f8d7da' : 
                                             isCompleted ? '#f0f8ff' : 'transparent',
                              color: selectedRestaurant === store["Store Number"] ? 'white' : 'inherit'
                            }}>
                              <TableCell 
                                sx={{ 
                                  color: 'inherit',
                                  cursor: 'pointer',
                                  fontWeight: selectedRestaurant === store["Store Number"] ? 600 : 400,
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: selectedRestaurant === store["Store Number"] ? '#1976d2' : 
                                                 isIncomplete ? '#f8d7da' : 
                                                 isCompleted ? '#f0f8ff' : 'white',
                                  zIndex: 5,
                                  '&:hover': { backgroundColor: '#4caf50', cursor: 'pointer' }
                                }}
                                onClick={() => setSelectedRestaurant(selectedRestaurant === store["Store Number"] ? null : store["Store Number"])}
                              >
                                {store["Store Number"]} - {store["Store Name"]}
                              </TableCell>
                              <TableCell align="center" sx={{ backgroundColor: 'inherit', display: 'none' }}>
                                {utilityFunctions.getUnitNumber(store["Store Number"])}
                              </TableCell>
                              <TableCell align="center" sx={{ backgroundColor: 'inherit' }}>
                                {(() => {
                                  const state = utilityFunctions.getState(store["Store Number"]);
                                  return ['OH', 'WV', 'PA'].includes(state) ? 'TS' : state;
                                })()}
                              </TableCell>
                              <TableCell align="center" sx={{ backgroundColor: 'inherit' }}>
                                {store["DM"] || 'N/A'}
                              </TableCell>
                              <TableCell align="center" sx={{ backgroundColor: 'inherit' }}>
                                {utilityFunctions.getDMEvaluationDate(store["Store Number"])}
                              </TableCell>
                              <TableCell align="center" sx={{ backgroundColor: 'inherit' }}>
                                {utilityFunctions.getLatestDMScore(store["Store Number"])}
                              </TableCell>

                              {[0, 1, 2, 3, 4].map(weekIndex => 
                                generateWeekCells(weekEvals, weekIndex as any, selectedRestaurant === store["Store Number"])
                              ).flat()}
                              {!showMoreWeeks && <TableCell sx={{ backgroundColor: selectedRestaurant === store["Store Number"] ? '#1976d2' : 'white' }}></TableCell>}
                              {showMoreWeeks && (
                                <>
                                  {[5, 6, 7, 8, 9].map(weekIndex => 
                                    generateWeekCells(weekEvals, weekIndex as any, selectedRestaurant === store["Store Number"])
                                  ).flat()}
                                  <TableCell sx={{ backgroundColor: selectedRestaurant === store["Store Number"] ? '#1976d2' : 'white' }}></TableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        });
                      })()}
                      <TableRow sx={{ 
                        borderTop: '2px solid #374151',
                        backgroundColor: '#f1f5f9'
                      }}>
                        <TableCell sx={{ fontSize: '16px', fontWeight: 700 }}>TOTAL</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>
                          {(() => {
                            const currentStores = utilityFunctions.getStoresForMarket(activeTab);
                            let filteredStores = currentStores;
                            if (completionFilter === 'Completed') {
                              filteredStores = currentStores.filter((store: StoreType) => utilityFunctions.getGlobalDate(store["Store Number"]) !== 'N/A');
                            } else if (completionFilter === 'Not Completed') {
                              filteredStores = currentStores.filter((store: StoreType) => utilityFunctions.getGlobalDate(store["Store Number"]) === 'N/A');
                            }
                            const completedCount = filteredStores.filter((store: StoreType) => utilityFunctions.getGlobalDate(store["Store Number"]) !== 'N/A').length;
                            return `${filteredStores.length} Stores (${completedCount} Completed)`;
                          })()
                        }</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>-</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>-</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>-</TableCell>
                        <TableCell align="center" sx={{ fontSize: '16px', fontWeight: 700 }}>-</TableCell>
                        {[0, 1, 2, 3, 4].map(() => 
                          Array(9).fill(null).map((_, i) => (
                            <TableCell key={i} sx={{ backgroundColor: '#f1f5f9' }}></TableCell>
                          ))
                        ).flat()}
                        {!showMoreWeeks && <TableCell sx={{ backgroundColor: '#f1f5f9' }}></TableCell>}
                        {showMoreWeeks && (
                          <>
                            {[5, 6, 7, 8, 9].map(() => 
                              Array(9).fill(null).map((_, i) => (
                                <TableCell key={i} sx={{ backgroundColor: '#f1f5f9' }}></TableCell>
                              ))
                            ).flat()}
                            <TableCell sx={{ backgroundColor: '#f1f5f9' }}></TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <div style={{
                padding: '48px',
                textAlign: 'center',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  color: '#6b7280',
                  margin: '0 0 16px 0'
                }}>
                  {activeTab ? 'No Stores Found' : 'Select a Market'}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#9ca3af',
                  margin: 0
                }}>
                  {activeTab ? 'No stores available for this market' : 'Choose a market tab to view restaurants'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(TrueViewTable);